import { QuickFiltersState, RecurrenceFilter } from '../components/quick-filters';
import type { FilterState } from '../rule-exceptions-table';
import {
  DEFAULT_FILTERS,
  DEFAULT_QUICK_FILTERS,
  useRuleExceptionsFiltersShallowStore,
} from './use-rule-exceptions-filters-store';

const RECURRING_TYPES = ['WEEKLY', 'CUSTOM', 'SPECIFIC_DATE'] as const;

const mapDetailedRecurrenceToQuick = (recurrenceTypes?: FilterState['recurrenceTypes']): RecurrenceFilter => {
  if (!recurrenceTypes?.length) return null;
  if (recurrenceTypes.every((type) => type === 'SINGLE_OCCURRENCE')) return 'SINGLE_ONLY';
  if (RECURRING_TYPES.every((type) => recurrenceTypes.includes(type))) return 'RECURRING_ONLY';
  return null;
};

const mapQuickRecurrenceToDetailed = (recurrence: RecurrenceFilter): FilterState['recurrenceTypes'] => {
  if (recurrence === 'SINGLE_ONLY') return ['SINGLE_OCCURRENCE'];
  if (recurrence === 'RECURRING_ONLY') return [...RECURRING_TYPES];
  return [];
};

export const useRuleExceptionsFilters = (locationId: string) => {
  const { filtersMap, quickFiltersMap, setFilters, setQuickFilters } = useRuleExceptionsFiltersShallowStore(
    'filtersMap',
    'quickFiltersMap',
    'setFilters',
    'setQuickFilters'
  );

  const filters = filtersMap[locationId] ?? DEFAULT_FILTERS;
  const quickFilters = quickFiltersMap[locationId] ?? DEFAULT_QUICK_FILTERS;

  const handleQuickFiltersChange = (nextQuickFilters: QuickFiltersState) => {
    setFilters(locationId, (prev) => ({
      ...prev,
      view: nextQuickFilters.view || undefined,
      recurrenceTypes: mapQuickRecurrenceToDetailed(nextQuickFilters.recurrence),
    }));
    setQuickFilters(locationId, nextQuickFilters);
  };

  const handleFiltersChange: React.Dispatch<React.SetStateAction<FilterState>> = (update) => {
    setFilters(locationId, (prev) => {
      const nextDetailedFilters = typeof update === 'function' ? update(prev) : update;
      setQuickFilters(locationId, {
        view: nextDetailedFilters.view ?? null,
        recurrence: mapDetailedRecurrenceToQuick(nextDetailedFilters.recurrenceTypes),
      });
      return nextDetailedFilters;
    });
  };

  return {
    filters,
    quickFilters,
    handleFiltersChange,
    handleQuickFiltersChange,
  };
};
